import React, { useEffect, useState } from 'react';
import { Mail, CheckCircle2, AlertTriangle, RefreshCw, PlusCircle, Copy, CornerUpLeft } from 'lucide-react';
import { toast } from 'sonner';
import { AdminAPI } from '../../api';

const STATUS = {
  verified: { label: 'Verified', cls: 'bg-[#E6F4F2] text-[#0B5F58]', icon: CheckCircle2 },
  pending: { label: 'Pending DNS', cls: 'bg-[#FDF0E6] text-[#B45309]', icon: AlertTriangle },
  not_started: { label: 'Awaiting DNS records', cls: 'bg-[#FDF0E6] text-[#B45309]', icon: AlertTriangle },
  not_added: { label: 'Not added to Resend', cls: 'bg-[#FEF2F2] text-[#B91C1C]', icon: AlertTriangle },
  failure: { label: 'Verification failed', cls: 'bg-[#FEF2F2] text-[#B91C1C]', icon: AlertTriangle },
};

const copy = (value, label) => {
  navigator.clipboard.writeText(value).then(
    () => toast.success(`${label} copied`),
    (err) => { console.error('[email-domain] clipboard failed', err); toast.error('Could not copy'); },
  );
};

const Row = ({ r }) => (
  <tr className="border-t border-[#E3E5F5]" data-testid={`dns-record-${r.type}-${r.name}`}>
    <td className="py-2 pr-3 font-semibold text-[#0A1551] whitespace-nowrap">{r.type}</td>
    <td className="py-2 pr-3 text-[#4A517D] whitespace-nowrap">
      <button onClick={() => copy(r.name, 'Name')} className="inline-flex items-center gap-1 hover:text-[#0F766E]" data-testid={`dns-copy-name-${r.type}-${r.name}`}>
        {r.name} <Copy className="w-3 h-3 opacity-50" />
      </button>
    </td>
    <td className="py-2 pr-3 text-[#4A517D] break-all">
      <button onClick={() => copy(r.value, 'Value')} className="inline-flex items-start gap-1 text-left hover:text-[#0F766E]" data-testid={`dns-copy-value-${r.type}-${r.name}`}>
        {r.value}{r.priority ? ` (priority ${r.priority})` : ''} <Copy className="w-3 h-3 mt-0.5 shrink-0 opacity-50" />
      </button>
    </td>
    <td className="py-2 text-[#6F76A7] whitespace-nowrap">{r.status}</td>
  </tr>
);

export const EmailDomainCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => AdminAPI.emailDomain().then(setData).catch((e) => { console.error('[email-domain] load failed', e); setData({ configured: false }); });
  useEffect(() => { load(); }, []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      toast[res.success ? 'success' : 'error'](res.detail || (res.success ? okMsg : 'Request failed'));
      await load();
    } catch (e) {
      console.error('[email-domain] action failed', e);
      toast.error('Request failed');
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="bg-white border border-[#E3E5F5] rounded-xl p-5 text-[13px] text-[#6F76A7]">Checking sending domain…</div>;

  const s = STATUS[data.status] || { label: data.configured ? 'Unknown' : 'Built-in sender', cls: 'bg-[#F0F1FB] text-[#4A517D]', icon: Mail };
  const Icon = s.icon;
  const needsAdding = data.status === 'not_added';

  return (
    <div className="bg-white border border-[#E3E5F5] rounded-xl p-5" data-testid="email-domain-card">
      <div className="flex flex-wrap items-center gap-3">
        <span className="w-9 h-9 rounded-lg bg-[#F0F1FB] flex items-center justify-center"><Mail className="w-4 h-4 text-[#0A1551]" /></span>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-bold text-[#0A1551]">Sending domain</p>
          <p className="text-[12px] text-[#6F76A7] truncate" data-testid="email-domain-from">{data.from || 'Built-in sender'}</p>
          {data.reply_to && (
            <p className="text-[12px] text-[#6F76A7] truncate flex items-center gap-1 mt-0.5" data-testid="email-domain-reply-to">
              <CornerUpLeft className="w-3 h-3" /> Replies go to {data.reply_to}
            </p>
          )}
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold ${s.cls}`} data-testid="email-domain-status">
          <Icon className="w-3.5 h-3.5" /> {s.label}
        </span>
        {needsAdding && (
          <button onClick={() => run(AdminAPI.addEmailDomain, 'Domain added')} disabled={busy} data-testid="email-domain-add"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#0F766E] hover:bg-[#0B5F58] text-white text-[13px] font-semibold disabled:opacity-50">
            <PlusCircle className="w-3.5 h-3.5" /> Add to Resend
          </button>
        )}
        <button onClick={() => run(AdminAPI.verifyEmailDomain, 'Verification requested — DNS can take a few minutes')} disabled={busy} data-testid="email-domain-verify"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#D5D7ED] text-[13px] font-semibold text-[#0A1551] hover:bg-[#F5F6FB] disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} /> Check verification
        </button>
      </div>

      {needsAdding && (
        <p className="text-[12px] text-[#B45309] mt-4" data-testid="email-domain-add-hint">
          <strong>{data.domain}</strong> is not registered with Resend yet. Press “Add to Resend” — the DNS records you need will appear right here.
        </p>
      )}

      {data.status !== 'verified' && (data.records || []).length > 0 && (
        <>
          <p className="text-[12px] text-[#6F76A7] mt-4">Add these records at your DNS provider for <strong>{data.domain}</strong>, then press “Check verification”. Click any name or value to copy it. Emails keep sending through the built-in sender until this is verified.</p>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="email-domain-records">
              <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#6F76A7]"><th className="pb-1 pr-3">Type</th><th className="pb-1 pr-3">Name</th><th className="pb-1 pr-3">Value</th><th className="pb-1">State</th></tr></thead>
              <tbody>{data.records.map((r) => <Row key={`${r.type}-${r.name}`} r={r} />)}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default EmailDomainCard;
